import { Component, Input, OnInit, OnDestroy, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ReleaseHistoryDialogComponent } from './release-history-dialog/release-history-dialog.component';
import { VariableComparerService } from './services/variable-comparer.service';
import { TaskComparerService } from './services/task-comparer.service';
import { HistoryService } from './services/history.service';
import { HistoryDocument } from './models/history-document';
import { EnvironmentService } from '../services/environment.service';
import { Environment } from '../models/environment';
import { AdoService } from '../services/ado.service';
import { Observable, of, finalize, catchError } from 'rxjs';
import { ReleaseDefinition } from '../models/release-definition';
import { ReleaseDefinitionRevision } from '../models/release-definition-revision';
import { ServiceConnection } from './models/service-connections';
import { LoadingService } from '../services/loading.service';

@Component({
    selector: 'app-mario',
    templateUrl: './mario.component.html',
    styleUrls: ['./mario.component.css'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class MarioComponent implements OnInit, OnDestroy {
  title = 'mario';
  before: HistoryDocument | undefined;
  after: HistoryDocument | undefined;
  beforeLabel = 'Before';
  afterLabel = 'After';
  defaultOrg!: string;
  defaultProject!: string;
  environment: Environment;
  releaseDefinitions: Array<ReleaseDefinition>;
  filteredReleaseDefinitionsBefore: Observable<Array<ReleaseDefinition>>;
  filteredReleaseDefinitionsAfter: Observable<Array<ReleaseDefinition>>;
  beforeReleaseDefinition: ReleaseDefinition | undefined;
  afterReleaseDefinition: ReleaseDefinition | undefined;
  serviceConnections: ServiceConnection[] = [];
  @Input() inRcJit = false;
  @Input() lastUnlockedReleaseRevision = '';
  @Input() currentReleaseRevison = '';
  private readonly subscriptions: any[] = [];

  constructor(
    private dialog: MatDialog,
    private environmentService: EnvironmentService,
    private adoService: AdoService,
    private variableComparer: VariableComparerService,
    private taskComparer: TaskComparerService,
    private readonly historyService: HistoryService,
    public readonly loadingService: LoadingService
  ) {
    this.environment = this.environmentService.getEnvironment();
    this.defaultOrg = this.environment.defaultOrg;
    this.defaultProject = this.environment.defaultProject;
    this.releaseDefinitions = new Array<ReleaseDefinition>();
    this.filteredReleaseDefinitionsBefore = of(this.releaseDefinitions);
    this.filteredReleaseDefinitionsAfter = of(this.releaseDefinitions);
  }

  ngOnInit(): void {
    this.loadReleaseDefinitions(this.defaultOrg, this.defaultProject);
    this.loadServiceConnections(this.defaultOrg, this.defaultProject);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.hasRevisionChanges(changes)) {
      this.loadRevisions();
      this.compareResults();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub?.unsubscribe());
  }

  loadReleaseDefinitions(org: string, project: string): void {
    this.loadingService.show('releaseDefinitions');
    this.releaseDefinitions = [];
    this.subscriptions.push(
      this.adoService.GetReleaseDefinitions(org, project).pipe(
        catchError(error => {
          console.error('Failed to load release definitions:', error);
          return of({ value: [] });
        }),
        finalize(() => this.loadingService.hide('releaseDefinitions'))
      ).subscribe((result: any) => {
        result.value?.forEach((def: any) => {
          this.releaseDefinitions.push({
            name: def.name,
            id: def.id,
          });
        });
        this.releaseDefinitions.sort(this.compareNameSort);
        this.updateBeforeFilteredReleaseDefinitions();
        this.updateAfterFilteredReleaseDefinitions();
      })
    );
  }

  loadServiceConnections(org: string, project: string): void {
    this.loadingService.show('serviceConnections');
    this.serviceConnections = [];
    this.subscriptions.push(
      this.adoService.GetServiceConnections(org, project).pipe(
        catchError(error => {
          console.error('Failed to load service connections:', error);
          return of({ value: [] });
        }),
        finalize(() => this.loadingService.hide('serviceConnections'))
      ).subscribe((result: any) => {
        result.value?.forEach((sc: any) => {
          this.serviceConnections.push({
            name: sc.name,
            id: sc.id,
          });
        });
      })
    );
  }

  updateBeforeFilteredReleaseDefinitions(searchTerm: string = ''): void {
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    this.filteredReleaseDefinitionsBefore = of(
      this.releaseDefinitions.filter(releaseDefinition =>
        releaseDefinition.name.toLowerCase().includes(lowerCaseSearchTerm)
      )
    );
  }

  updateAfterFilteredReleaseDefinitions(searchTerm: string = ''): void {
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    this.filteredReleaseDefinitionsAfter = of(
      this.releaseDefinitions.filter(releaseDefinition =>
        releaseDefinition.name.toLowerCase().includes(lowerCaseSearchTerm)
      )
    );
  }

  displayRcName(rcDef?: ReleaseDefinition): string {
    return rcDef ? rcDef.name : '';
  }

  compareNameSort(a: ReleaseDefinition, b: ReleaseDefinition): number {
    let nameOne = a.name.toLowerCase();
    let nameTwo = b.name.toLowerCase();
    let compareResult = 0;
    if (nameOne > nameTwo) compareResult = 1;
    if (nameOne < nameTwo) compareResult = -1;
    return compareResult;
  }

  loadRevisions(): void {
    if (this.lastUnlockedReleaseRevision && this.currentReleaseRevison) {
      this.before = this.historyService.getHistory(this.lastUnlockedReleaseRevision);
      this.after = this.historyService.getHistory(this.currentReleaseRevison);
    }
  }

  hasRevisionChanges(changes: SimpleChanges): boolean {
    return 'lastUnlockedReleaseRevision' in changes || 'currentReleaseRevison' in changes;
  }

  compareResults(): void {
    if (this.isComparable()) {
      this.compareVariables();
      this.compareTasks();
    }
  }

  isComparable(): boolean {
    return this.before !== undefined && this.after !== undefined;
  }

  compareVariables(): void {
    const { before, after } = this.variableComparer.compare(this.before!.variables, this.after!.variables);
    this.before!.variables = before;
    this.after!.variables = after;
  }

  compareTasks(): void {
    const { before, after } = this.taskComparer.compare(this.before!.environments, this.after!.environments);
    this.before!.environments = before;
    this.after!.environments = after;
  }

  openDialog(releaseDefinition: ReleaseDefinition, label: string): void {
    this.loadingService.show('releaseHistory');
    this.subscriptions.push(
      this.adoService
        .GetReleaseDefinitionHistory(this.defaultOrg, this.defaultProject, releaseDefinition.id.toString())
        .pipe(
          catchError(error => {
            console.error('Failed to load release history:', error);
            return of({ value: [] });
          }),
          finalize(() => this.loadingService.hide('releaseHistory'))
        )
        .subscribe((result: any) => {
          const releaseDefHistory = this.processReleaseHistory(result);
          this.openReleaseHistoryDialog(releaseDefHistory, releaseDefinition, label);
        })
    );
  }

  processReleaseHistory(result: any): ReleaseDefinitionRevision[] {
    return result.value
      .sort((a: ReleaseDefinitionRevision, b: ReleaseDefinitionRevision) => b.revision - a.revision)
      .map((revision: ReleaseDefinitionRevision) => ({
        revision: revision.revision,
        changedBy: revision.changedBy.displayName,
        changeType: this.capitalizeFirstLetter(revision.changeType),
        changedDate: new Date(revision.changedDate).toLocaleString(),
        comment: revision.comment
      }));
  }

  openReleaseHistoryDialog(releaseDefHistory: ReleaseDefinitionRevision[], releaseDefinition: ReleaseDefinition, label: string): void {
    const dialogRef = this.dialog.open(ReleaseHistoryDialogComponent, {
      width: '80vw',
      height: '80vh',
      data: { releaseDefHistory },
      autoFocus: false,
    });



    dialogRef.afterClosed().subscribe((result: ReleaseDefinitionRevision) => {
      if (result) {
        this.loadReleaseDefinitionRevision(releaseDefinition, result.revision, label);
      }
    });
  }

  loadReleaseDefinitionRevision(releaseDefinition: ReleaseDefinition, revision: number, label: string): void {
    this.loadingService.show('releaseDefinitionRevision');
    this.subscriptions.push(
      this.adoService
        .GetReleaseDefinitionRevision(this.defaultOrg, this.defaultProject, releaseDefinition.id.toString(), revision.toString())
        .pipe(
          finalize(() => this.loadingService.hide('releaseDefinitionRevision'))
        )
        .subscribe(result => {
          const releaseDefJson = JSON.stringify(result);
          const history = this.historyService.getHistory(releaseDefJson);
          if (label === this.beforeLabel) {
            this.before = history;
          } else {
            this.after = history;
          }
          this.compareResults();
        })
    );
  }

  capitalizeFirstLetter(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  reset(): void {
    this.before = undefined;
    this.after = undefined;
    this.beforeReleaseDefinition = undefined;
    this.afterReleaseDefinition = undefined;
    this.filteredReleaseDefinitionsBefore = of(this.releaseDefinitions);
    this.filteredReleaseDefinitionsAfter = of(this.releaseDefinitions);
  }
}
